import React, { useState, useEffect } from 'react';
import api from '../utils/api';

const ReferenceManagement = () => {
  const [types, setTypes] = useState([]);
  const [values, setValues] = useState([]);
  const [selectedTypeId, setSelectedTypeId] = useState(null);
  const [typeForm, setTypeForm] = useState({ id: null, type_name: '', description: '', is_active: true });
  const [valueForm, setValueForm] = useState({ id: null, type_id: '', key_name: '', value: '', display_order: 0, is_active: true });
  const [error, setError] = useState(null);
  const [editingType, setEditingType] = useState(false);
  const [editingValue, setEditingValue] = useState(false);

  // Fetch all reference types
  const fetchTypes = async () => {
    try {
      const res = await api.get('/api/reference_types');
      setTypes(res.data);
      setError(null);
    } catch (err) {
      setError('Failed to fetch reference types.');
    }
  };

  // Fetch all reference values for selected type
  const fetchValues = async (typeId) => {
    try {
      const res = await api.get('/api/reference_values', { params: { type_id: typeId } });
      setValues(res.data);
      setError(null);
    } catch (err) {
      setError('Failed to fetch reference values.');
    }
  };

  useEffect(() => {
    fetchTypes();
  }, []);

  useEffect(() => {
    if (selectedTypeId) fetchValues(selectedTypeId);
    else setValues([]);
  }, [selectedTypeId]);

  // Handlers for Reference Types
  const handleTypeFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setTypeForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleTypeSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingType) {
        await api.put(`/api/reference_types/${typeForm.id}`, typeForm);
      } else {
        await api.post('/api/reference_types', typeForm);
      }
      setTypeForm({ id: null, type_name: '', description: '', is_active: true });
      setEditingType(false);
      fetchTypes();
    } catch (err) {
      setError('Failed to save reference type.');
    }
  };

  const handleTypeEdit = (type) => {
    setTypeForm(type);
    setEditingType(true);
  };

  const handleTypeDelete = async (id) => {
    if (window.confirm('Delete this reference type?')) {
      try {
        await api.delete(`/api/reference_types/${id}`);
        if (selectedTypeId === id) setSelectedTypeId(null);
        fetchTypes();
      } catch (err) {
        setError('Failed to delete reference type.');
      }
    }
  };

  // Handlers for Reference Values
  const handleValueFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setValueForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleValueSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingValue) {
        await api.put(`/api/reference_values/${valueForm.id}`, valueForm);
      } else {
        await api.post('/api/reference_values', { ...valueForm, type_id: selectedTypeId });
      }
      setValueForm({ id: null, type_id: '', key_name: '', value: '', display_order: 0, is_active: true });
      setEditingValue(false);
      fetchValues(selectedTypeId);
    } catch (err) {
      setError('Failed to save reference value.');
    }
  };

  const handleValueEdit = (val) => {
    setValueForm(val);
    setEditingValue(true);
  };

  const handleValueDelete = async (id) => {
    if (window.confirm('Delete this reference value?')) {
      try {
        await api.delete(`/api/reference_values/${id}`);
        fetchValues(selectedTypeId);
      } catch (err) {
        setError('Failed to delete reference value.');
      }
    }
  };

  return (
    <div className="container py-4">
      <h2 className="mb-4">Reference Types</h2>
      {error && <div className="alert alert-danger">{error}</div>}
      <form onSubmit={handleTypeSubmit} className="row g-2 align-items-end mb-3">
        <div className="col-md-3">
          <input
            type="text"
            name="type_name"
            className="form-control"
            placeholder="Type Name"
            value={typeForm.type_name}
            onChange={handleTypeFormChange}
            required
          />
        </div>
        <div className="col-md-3">
          <input
            type="text"
            name="description"
            className="form-control"
            placeholder="Description"
            value={typeForm.description}
            onChange={handleTypeFormChange}
          />
        </div>
        <div className="col-md-2 form-check form-switch">
          <input
            type="checkbox"
            name="is_active"
            className="form-check-input"
            id="typeActiveSwitch"
            checked={typeForm.is_active}
            onChange={handleTypeFormChange}
          />
          <label className="form-check-label" htmlFor="typeActiveSwitch">Active</label>
        </div>
        <div className="col-md-2">
          <button type="submit" className="btn btn-primary me-2">{editingType ? 'Update' : 'Add'}</button>
          {editingType && (
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => { setTypeForm({ id: null, type_name: '', description: '', is_active: true }); setEditingType(false); }}
            >Cancel</button>
          )}
        </div>
      </form>
      <div className="table-responsive mb-4">
        <table className="table table-bordered table-hover table-sm align-middle">
          <thead className="table-light">
            <tr>
              <th>ID</th>
              <th>Type Name</th>
              <th>Description</th>
              <th>Active?</th>
              <th>Created</th>
              <th>Updated</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {types.map(type => (
              <tr key={type.id} className={selectedTypeId === type.id ? 'table-info' : ''}>
                <td>{type.id}</td>
                <td>
                  <a href="#!" onClick={() => setSelectedTypeId(type.id)} className="text-decoration-underline">{type.type_name}</a>
                </td>
                <td>{type.description}</td>
                <td>{type.is_active ? 'Yes' : 'No'}</td>
                <td>{type.created_at ? new Date(type.created_at).toLocaleString() : ''}</td>
                <td>{type.updated_at ? new Date(type.updated_at).toLocaleString() : ''}</td>
                <td>
                  <button className="btn btn-sm btn-outline-primary me-1" onClick={() => handleTypeEdit(type)}>Edit</button>
                  <button className="btn btn-sm btn-outline-danger" onClick={() => handleTypeDelete(type.id)}>Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {selectedTypeId && (
        <div>
          <h3 className="mb-3">
            Reference Values for Type: <span className="text-primary">{types.find(t => t.id === selectedTypeId)?.type_name}</span>
          </h3>
          <form onSubmit={handleValueSubmit} className="row g-2 align-items-end mb-3">
            <div className="col-md-3">
              <input
                type="text"
                name="key_name"
                className="form-control"
                placeholder="Key Name"
                value={valueForm.key_name}
                onChange={handleValueFormChange}
                required
              />
            </div>
            <div className="col-md-3">
              <input
                type="text"
                name="value"
                className="form-control"
                placeholder="Value"
                value={valueForm.value}
                onChange={handleValueFormChange}
              />
            </div>
            <div className="col-md-2">
              <input
                type="number"
                name="display_order"
                className="form-control"
                placeholder="Display Order"
                value={valueForm.display_order}
                onChange={handleValueFormChange}
                style={{ width: 90 }}
              />
            </div>
            <div className="col-md-2 form-check form-switch">
              <input
                type="checkbox"
                name="is_active"
                className="form-check-input"
                id="valueActiveSwitch"
                checked={valueForm.is_active}
                onChange={handleValueFormChange}
              />
              <label className="form-check-label" htmlFor="valueActiveSwitch">Active</label>
            </div>
            <div className="col-md-2">
              <button type="submit" className="btn btn-primary me-2">{editingValue ? 'Update' : 'Add'}</button>
              {editingValue && (
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => { setValueForm({ id: null, type_id: '', key_name: '', value: '', display_order: 0, is_active: true }); setEditingValue(false); }}
                >Cancel</button>
              )}
            </div>
          </form>
          <div className="table-responsive">
            <table className="table table-bordered table-hover table-sm align-middle">
              <thead className="table-light">
                <tr>
                  <th>ID</th>
                  <th>Key Name</th>
                  <th>Value</th>
                  <th>Order</th>
                  <th>Active?</th>
                  <th>Created</th>
                  <th>Updated</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {values.map(val => (
                  <tr key={val.id}>
                    <td>{val.id}</td>
                    <td>{val.key_name}</td>
                    <td>{val.value}</td>
                    <td>{val.display_order}</td>
                    <td>{val.is_active ? 'Yes' : 'No'}</td>
                    <td>{val.created_at ? new Date(val.created_at).toLocaleString() : ''}</td>
                    <td>{val.updated_at ? new Date(val.updated_at).toLocaleString() : ''}</td>
                    <td>
                      <button className="btn btn-sm btn-outline-primary me-1" onClick={() => handleValueEdit(val)}>Edit</button>
                      <button className="btn btn-sm btn-outline-danger" onClick={() => handleValueDelete(val.id)}>Delete</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReferenceManagement;